import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { chatAPI } from '../lib/api';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function ChatWidget() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEnd = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: 'assistant', content: t('chat.welcome') }]);
    }
  }, [open, messages.length, t]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const res = await chatAPI.send({ message: userMsg, session_id: sessionId });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Please email us at info@xdomainx.com' }]);
    }
    setLoading(false);
  };

  return (
    <>
      {/* Toggle Button */}
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="chat-toggle-btn"
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#00E5FF] text-black flex items-center justify-center shadow-[0_0_30px_rgba(0,229,255,0.4)] hover:shadow-[0_0_40px_rgba(0,229,255,0.7)] transition-shadow">
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-3rem)] h-[500px] flex flex-col rounded-sm border border-white/10 bg-[#0A0A0A] shadow-2xl animate-fade-up" data-testid="chat-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#050505]">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#00FF94] animate-pulse" />
              <span className="text-sm font-semibold text-white">{t('chat.title')} - XDomainX</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white" data-testid="chat-close-btn">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-sm text-sm leading-relaxed ${msg.role === 'user' ? 'bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/20' : 'bg-zinc-900 text-zinc-300 border border-white/5'}`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-zinc-900 border border-white/5 px-3 py-2 rounded-sm">
                  <Loader2 className="h-4 w-4 animate-spin text-[#00E5FF]" />
                </div>
              </div>
            )}
            <div ref={messagesEnd} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-white/10 bg-[#050505]">
            <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t('chat.placeholder')}
                className="flex-1 bg-[#0A0A0A] border border-zinc-800 focus:border-[#00E5FF] text-white text-sm px-3 py-2 rounded-sm outline-none transition-colors" data-testid="chat-input" />
              <Button type="submit" disabled={loading || !input.trim()} size="sm"
                className="bg-[#00E5FF] text-black hover:bg-white rounded-sm px-3" data-testid="chat-send-btn">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
